import React, { useState, useMemo, useCallback } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

// ─── constants ────────────────────────────────────────────────────────────────
const SVC_ORDER = [
  "recharge","dth","bbps","aeps","dmt",
  "matm","cms","pan_card","insurance","fastag","lic","wallet_settlement","upi_qr",
];
const SVC_META = {
  recharge:          { label: "Mobile Recharge",    icon: "ph-device-mobile" },
  dth:               { label: "DTH Recharge",       icon: "ph-television-simple" },
  bbps:              { label: "Bill Payment (BBPS)", icon: "ph-file-text" },
  aeps:              { label: "AEPS",                icon: "ph-fingerprint" },
  dmt:               { label: "DMT Transfer",        icon: "ph-paper-plane-right" },
  matm:              { label: "MATM",                icon: "ph-credit-card" },
  cms:               { label: "CMS",                 icon: "ph-money" },
  pan_card:          { label: "PAN Card",            icon: "ph-identification-card" },
  insurance:         { label: "Insurance",           icon: "ph-shield-check" },
  fastag:            { label: "FasTag",              icon: "ph-car" },
  lic:               { label: "LIC Premium",         icon: "ph-heart" },
  wallet_settlement: { label: "Wallet Settlement",   icon: "ph-wallet" },
  upi_qr:            { label: "UPI QR",              icon: "ph-qr-code" },
};

const inr = (n) =>
  "₹ " + (parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const getTodayRange = () => {
  const t = new Date().toISOString().split("T")[0];
  return { start: t, end: t };
};

const buildUrl = (userid, start, end, targetUser = "") => {
  let url = `https://api.mokshiq.in/user_wise_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (targetUser) url += `&target_user=${targetUser}`;
  return url;
};

// ─── stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, colorClass, bgClass }) {
  return (
    <div className={`card hover-effect ${bgClass} mb-0`}>
      <div className="card-body d-flex align-items-center justify-content-between">
        <div>
          <p className="mb-1 text-secondary f-s-13 f-w-500">{label}</p>
          <h4 className={`mb-0 f-w-700 ${colorClass}`}>{value}</h4>
        </div>
        <span className={`h-50 w-50 d-flex-center b-r-50 ${colorClass.replace("text-","bg-")}`}>
          <i className={`ph-duotone ${icon} f-s-26 text-white`}></i>
        </span>
      </div>
    </div>
  );
}

// ─── operator mini list ───────────────────────────────────────────────────────
function OperatorList({ operators }) {
  if (!operators || !Object.keys(operators).length)
    return <span className="text-secondary f-s-11">—</span>;
  return (
    <div className="mt-1">
      {Object.entries(operators).map(([op, v], i) => (
        <div key={i} className="d-flex justify-content-between gap-3 py-1"
          style={{ borderBottom: i < Object.keys(operators).length-1 ? "1px dashed #e5e7eb" : "none" }}>
          <span className="f-s-11 text-secondary"><i className="ph ph-dot-outline me-1"></i>{op}</span>
          <span className="f-s-11 f-w-500 text-primary">{v.txn} txn · {inr(v.amount)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── daily balance table ──────────────────────────────────────────────────────
function DailyBalanceTable({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mb-3">
      <div className="f-s-12 f-w-600 text-secondary mb-2">
        <i className="ph-duotone ph-calendar-check me-1"></i>Daily Wallet Balance
      </div>
      <div className="table-responsive">
        <table className="table table-bordered mb-0" style={{ fontSize: 12 }}>
          <thead style={{ background: "#f8f9fc" }}>
            <tr>
              <th className="f-s-11 f-w-700 text-secondary">Date</th>
              <th className="f-s-11 f-w-700 text-secondary text-end">Opening Balance</th>
              <th className="f-s-11 f-w-700 text-secondary text-end">Closing Balance</th>
              <th className="f-s-11 f-w-700 text-secondary text-end">Net Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const net = parseFloat((r.closing_balance - r.opening_balance).toFixed(2));
              return (
                <tr key={i}>
                  <td><span className="badge text-light-primary f-s-11 px-2">{r.date}</span></td>
                  <td className="text-end f-w-500">{inr(r.opening_balance)}</td>
                  <td className="text-end f-w-600 text-primary">{inr(r.closing_balance)}</td>
                  <td className="text-end">
                    <span className={`f-w-600 ${net >= 0 ? "text-success" : "text-danger"}`}>
                      {net >= 0 ? "+" : ""}{inr(net)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot style={{ background: "#eef2ff", borderTop: "2px solid #c7d2fe" }}>
              <tr>
                <td className="f-s-11 f-w-700 text-primary">Period Total</td>
                <td className="text-end f-s-12 f-w-700 text-secondary">{inr(rows[0].opening_balance)}</td>
                <td className="text-end f-s-12 f-w-700 text-primary">{inr(rows[rows.length-1].closing_balance)}</td>
                <td className="text-end f-s-12 f-w-700">
                  {(() => {
                    const net = parseFloat((rows[rows.length-1].closing_balance - rows[0].opening_balance).toFixed(2));
                    return <span className={net >= 0 ? "text-success" : "text-danger"}>{net >= 0 ? "+" : ""}{inr(net)}</span>;
                  })()}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ─── wallet reconciliation card ───────────────────────────────────────────────
function ReconciliationCard({ rec, wallet }) {
  if (!rec) return null;
  const diff = rec.difference || 0;
  const diffColor = Math.abs(diff) < 0.1 ? "text-success" : Math.abs(diff) < 5 ? "text-warning" : "text-danger";
  return (
    <div className="mb-3">
      <div className="f-s-12 f-w-600 text-secondary mb-2">
        <i className="ph-duotone ph-scales me-1"></i>Wallet Reconciliation
      </div>
      <div className="card mb-0" style={{ borderLeft: "3px solid #4361ee" }}>
        <div className="card-body py-2 px-3">
          {/* Formula header */}
          <div className="f-s-11 text-secondary mb-2">
            Opening + Fund In − Service Spend − Fund Out = Closing
          </div>
          <div className="row g-2">
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Opening</div>
              <div className="f-w-600 f-s-13">{inr(rec.opening_balance)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-down text-success me-1"></i>Fund In
              </div>
              <div className="f-w-600 f-s-13 text-success">+{inr(rec.fund_in)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-up text-warning me-1"></i>Service Spend
              </div>
              <div className="f-w-600 f-s-13 text-warning">−{inr(rec.service_spend)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">
                <i className="ph-fill ph-arrow-circle-up text-danger me-1"></i>Fund Out
              </div>
              <div className="f-w-600 f-s-13 text-danger">−{inr(rec.fund_out)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Expected Closing</div>
              <div className="f-w-600 f-s-13 text-primary">{inr(rec.expected_closing)}</div>
            </div>
            <div className="col-6 col-md-2">
              <div className="f-s-11 text-secondary">Actual Closing</div>
              <div className="f-w-600 f-s-13 text-primary">{inr(rec.actual_closing)}</div>
            </div>
          </div>
          {/* Difference */}
          <div className="mt-2 pt-2" style={{ borderTop: "1px solid #e9ecef" }}>
            <span className="f-s-11 text-secondary me-2">Difference:</span>
            <span className={`f-w-700 f-s-13 ${diffColor}`}>
              {diff >= 0 ? "+" : ""}{inr(diff)}
            </span>
            {Math.abs(diff) < 0.1 && (
              <span className="badge text-light-success f-s-10 ms-2">
                <i className="ph-fill ph-check-circle me-1"></i>Balanced
              </span>
            )}
            {Math.abs(diff) >= 0.1 && (
              <span className="f-s-11 text-secondary ms-2">
                (commission credits, charges, holds, pending txns)
              </span>
            )}
          </div>
          {/* Fund movement breakdown if any */}
          {wallet && (wallet.fund_in > 0 || wallet.fund_out > 0) && (
            <div className="mt-2 pt-2 d-flex gap-4" style={{ borderTop: "1px solid #e9ecef" }}>
              <div>
                <span className="f-s-11 text-secondary">Total Fund Received: </span>
                <span className="f-w-600 text-success f-s-12">{inr(wallet.fund_in)}</span>
              </div>
              <div>
                <span className="f-s-11 text-secondary">Total Fund Sent Out: </span>
                <span className="f-w-600 text-danger f-s-12">{inr(wallet.fund_out)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── expandable user row ──────────────────────────────────────────────────────
function UserRow({ user, isOpen, onToggle }) {
  const s    = user.summary;
  const rec  = user.reconciliation;
  const db   = user.daily_balances || [];
  const inits = (user.user_name || "U").trim().split(" ")
                  .map(p => p[0] || "").slice(0, 2).join("").toUpperCase();
  const activeSvcs = SVC_ORDER.filter(sl => user.services?.[sl]);

  return (
    <>
      {/* ── Summary row ── */}
      <tr className="cursor-pointer" onClick={onToggle} style={{ cursor: "pointer" }}>
        <td>
          <div className="d-flex align-items-center gap-2">
            <span className="d-flex-center b-r-50 f-w-600 f-s-13"
              style={{
                width: 36, height: 36, flexShrink: 0,
                background: user.wl_name ? "var(--bs-warning-bg-subtle,#fff3cd)" : "var(--bs-primary-bg-subtle,#e0e7ff)",
                color: user.wl_name ? "var(--bs-warning-text,#664d03)" : "var(--bs-primary,#4361ee)",
              }}>
              {inits}
            </span>
            <div>
              <div className="f-w-600 f-s-14">
                {user.user_name || "—"}
                {user.wl_name && (
                  <span className="badge text-light-warning f-s-10 ms-2">
                    <i className="ph-fill ph-buildings me-1"></i>WL
                  </span>
                )}
              </div>
              <div className="text-secondary f-s-12">{user.user_phone}</div>
              {user.wl_name && user.retailer_name !== user.user_name && (
                <div className="f-s-11" style={{ color: "var(--bs-warning-text,#664d03)" }}>
                  <i className="ph ph-user me-1"></i>Retailer: {user.retailer_name}
                </div>
              )}
            </div>
          </div>
        </td>
        <td><span className="badge text-light-secondary f-s-11">{user.role}</span></td>
        <td><span className="badge text-light-primary f-s-12 px-2">{s.total_txn}</span></td>
        <td>
          <span className="text-success f-w-700">
            <i className="ph-fill ph-check-circle me-1"></i>{s.success_txn}
          </span>
          <div className="text-danger f-s-11">{s.failed_txn} failed</div>
        </td>
        {/* Opening balance — from FIRST txn across ALL products */}
        <td className="f-w-500 text-secondary f-s-13">
          {db.length > 0 ? inr(db[0].opening_balance) : "—"}
        </td>
        {/* Closing balance — from LAST txn across ALL products */}
        <td className="f-w-600 text-primary f-s-13">
          {db.length > 0 ? inr(db[db.length-1].closing_balance) : "—"}
        </td>
        {/* Fund In */}
        <td className="f-w-600 text-success f-s-13">
          {rec ? inr(rec.fund_in) : "—"}
        </td>
        {/* Fund Out */}
        <td className="f-w-600 text-danger f-s-13">
          {rec ? inr(rec.fund_out) : "—"}
        </td>
        <td className="f-w-600 text-secondary">{inr(s.total_amount)}</td>
        <td className="f-w-700 text-success">{inr(s.success_amount)}</td>
        <td className="f-w-700 text-primary">{inr(s.total_commission)}</td>
        <td>
          <i className={`ph-bold ${isOpen ? "ph-caret-up" : "ph-caret-down"} text-secondary`}></i>
        </td>
      </tr>

      {/* ── Expanded detail ── */}
      {isOpen && (
        <tr>
          <td colSpan={12} style={{ padding: 0, background: "#f8f9fc" }}>
            <div className="p-3">

              {/* Daily balance table */}
              <DailyBalanceTable rows={db} />

              {/* Reconciliation */}
              <ReconciliationCard rec={rec} wallet={user.wallet_summary} />

              {/* Service cards */}
              {activeSvcs.length > 0 && (
                <>
                  <div className="f-s-12 f-w-600 text-secondary mb-2">
                    <i className="ph-duotone ph-squares-four me-1"></i>Service Breakdown
                  </div>
                  <div className="row g-2">
                    {activeSvcs.map(slug => {
                      const sv   = user.services[slug];
                      const meta = SVC_META[slug] || { label: slug, icon: "ph-circle" };
                      return (
                        <div className="col-md-6 col-xl-4" key={slug}>
                          <div className="card mb-0" style={{ borderLeft: "3px solid var(--bs-primary,#4361ee)" }}>
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <i className={`ph-duotone ${meta.icon} f-s-18 text-primary`}></i>
                                  <span className="f-w-600 f-s-13">{sv.label || meta.label}</span>
                                </div>
                                <span className="badge text-light-primary f-s-11">{sv.total_txn} txn</span>
                              </div>
                              <div className="d-flex gap-2 flex-wrap mb-2">
                                <span className="badge text-light-success f-s-11">
                                  <i className="ph-fill ph-check-circle me-1"></i>{sv.success_txn} success
                                </span>
                                <span className="badge text-light-danger f-s-11">
                                  <i className="ph-fill ph-x-circle me-1"></i>{sv.failed_txn} failed
                                </span>
                                {sv.pending_txn > 0 && (
                                  <span className="badge text-light-warning f-s-11">
                                    <i className="ph-fill ph-clock me-1"></i>{sv.pending_txn} pending
                                  </span>
                                )}
                              </div>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <div className="text-secondary f-s-11">Total amount</div>
                                  <div className="f-w-600 f-s-13">{inr(sv.total_amount)}</div>
                                </div>
                                <div className="text-end">
                                  <div className="text-secondary f-s-11">Success amt</div>
                                  <div className="f-w-600 f-s-13 text-success">{inr(sv.success_amount)}</div>
                                </div>
                                <div className="text-end">
                                  <div className="text-secondary f-s-11">Commission</div>
                                  <div className="f-w-600 f-s-13 text-primary">{inr(sv.commission)}</div>
                                </div>
                              </div>
                              {Object.keys(sv.operators || {}).length > 0 && (
                                <div className="mt-2 pt-2" style={{ borderTop: "1px solid #e9ecef" }}>
                                  <div className="text-secondary f-s-11 f-w-500 mb-1">Operator wise</div>
                                  <OperatorList operators={sv.operators} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
function UserWiseServiceReportPage() {
  const { userid } = useSession();
  const { start, end } = getTodayRange();

  const [fromDate, setFromDate]       = useState(start);
  const [toDate, setToDate]           = useState(end);
  const [targetUser, setTargetUser]   = useState("");
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [activeTab, setActiveTab]     = useState("users");
  const [page, setPage]               = useState(1);
  const PAGE_SIZE = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setExpandedKey(null);
    try {
      const url = buildUrl(userid, fromDate, toDate, targetUser);
      const res = await fetchWrapper(url);
      if (res.status) { setData(res); setPage(1); }
      else showMainToast(res.error || "Failed to load", "danger");
    } catch { showMainToast("Network error", "danger"); }
    finally { setLoading(false); }
  }, [userid, fromDate, toDate, targetUser]);

  const g      = data?.grand_total || {};
  const report = data?.report      || [];

  const pageRows = useMemo(() => {
    const s = (page - 1) * PAGE_SIZE;
    return report.slice(s, s + PAGE_SIZE);
  }, [report, page]);

  // By Service cross-user summary
  const serviceSummary = useMemo(() => {
    if (!report.length) return [];
    return SVC_ORDER.map(slug => {
      const meta = SVC_META[slug] || { label: slug, icon: "ph-circle" };
      let total_txn=0, success_txn=0, failed_txn=0, pending_txn=0;
      let total_amount=0, success_amount=0, commission=0;
      const opMap = {};
      report.forEach(u => {
        const sv = u.services?.[slug]; if (!sv) return;
        total_txn     += sv.total_txn      || 0;
        success_txn   += sv.success_txn    || 0;
        failed_txn    += sv.failed_txn     || 0;
        pending_txn   += sv.pending_txn    || 0;
        total_amount  += parseFloat(sv.total_amount)   || 0;
        success_amount+= parseFloat(sv.success_amount) || 0;
        commission    += parseFloat(sv.commission)     || 0;
        Object.entries(sv.operators || {}).forEach(([op, v]) => {
          if (!opMap[op]) opMap[op] = { txn: 0, amount: 0 };
          opMap[op].txn    += v.txn    || 0;
          opMap[op].amount += parseFloat(v.amount) || 0;
        });
      });
      if (total_txn === 0) return null;
      return { slug, ...meta, total_txn, success_txn, failed_txn, pending_txn,
               total_amount, success_amount, commission, operators: opMap };
    }).filter(Boolean);
  }, [report]);

  const exportCSV = () => {
    if (!report.length) { showMainToast("No data", "warning"); return; }
    const rows = [["BC Name","Phone","Retailer Name","Role","Opening Bal","Closing Bal",
                   "Fund In","Fund Out","Service","Total Txn","Success","Failed","Pending",
                   "Total Amount","Success Amt","Commission"].join(",")];
    report.forEach(u => {
      const db  = u.daily_balances || [];
      const rec = u.reconciliation || {};
      const ob  = db.length > 0 ? db[0].opening_balance : 0;
      const cb  = db.length > 0 ? db[db.length-1].closing_balance : 0;
      SVC_ORDER.forEach(slug => {
        const sv = u.services?.[slug]; if (!sv) return;
        rows.push([u.user_name, u.user_phone, u.retailer_name, u.role,
          ob, cb, rec.fund_in||0, rec.fund_out||0,
          sv.label||slug, sv.total_txn, sv.success_txn, sv.failed_txn, sv.pending_txn,
          sv.total_amount, sv.success_amount, sv.commission].join(","));
      });
    });
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.join("\n"));
    a.download = `user_wise_service_report_${fromDate}_to_${toDate}.csv`;
    a.click();
  };

  return (
    <main>
      <div className="container-fluid">

        {/* Breadcrumb */}
        <div className="row m-1">
          <div className="col-12 d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h4 className="main-title">User Wise Service Report</h4>
              <ul className="app-line-breadcrumbs mb-3">
                <li><a className="f-s-14 f-w-500" href="#">
                  <span className="d-flex align-items-center gap-1">
                    <i className="ph-duotone ph-chart-bar f-s-16"></i>Reports
                  </span>
                </a></li>
                <li className="active"><span className="f-s-14 f-w-500">User Wise Service</span></li>
              </ul>
            </div>
            <button className="btn btn-outline-primary mt-1" onClick={exportCSV}>
              <i className="ph-duotone ph-download-simple me-1"></i>Export CSV
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="card mb-3">
          <div className="card-header">
            <h5><i className="ph-duotone ph-funnel me-2"></i>Filter</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">From Date</label>
                <input type="date" className="form-control" value={fromDate}
                  onChange={e => setFromDate(e.target.value)} />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">To Date</label>
                <input type="date" className="form-control" value={toDate} min={fromDate}
                  onChange={e => setToDate(e.target.value)} />
              </div>
              <div className="col-md-3 col-6">
                <label className="form-label f-s-13 f-w-500">
                  Retailer ID <span className="text-secondary">(optional)</span>
                </label>
                <input type="text" className="form-control" placeholder="Filter to one retailer"
                  value={targetUser} onChange={e => setTargetUser(e.target.value)} />
              </div>
              <div className="col-md-3 col-6">
                <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Loading…</>
                    : <><i className="ph-bold ph-magnifying-glass me-2"></i>Fetch Report</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        {data && (
          <div className="row g-3 mb-3">
            <div className="col-md-2 col-6">
              <StatCard label="Total Users" value={data.total_users||0}
                icon="ph-users-three" colorClass="text-primary" bgClass="card-light-primary" />
            </div>
            <div className="col-md-2 col-6">
              <StatCard label="Total Txn" value={(g.total_txn||0).toLocaleString("en-IN")}
                icon="ph-receipt" colorClass="text-warning" bgClass="card-light-warning" />
            </div>
            <div className="col-md-2 col-6">
              <StatCard label="Fund In (Total)" value={inr(g.total_fund_in)}
                icon="ph-arrow-circle-down" colorClass="text-success" bgClass="card-light-success" />
            </div>
            <div className="col-md-2 col-6">
              <StatCard label="Fund Out (Total)" value={inr(g.total_fund_out)}
                icon="ph-arrow-circle-up" colorClass="text-danger" bgClass="card-light-danger" />
            </div>
            <div className="col-md-2 col-6">
              <StatCard label="Service Amount" value={inr(g.success_amount)}
                icon="ph-check-circle" colorClass="text-info" bgClass="card-light-info" />
            </div>
            <div className="col-md-2 col-6">
              <StatCard label="Commission" value={inr(g.total_commission)}
                icon="ph-coins" colorClass="text-secondary" bgClass="card-light-secondary" />
            </div>
          </div>
        )}

        {/* Tabs */}
        {data && (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <ul className="nav nav-tabs tab-light-primary border-0" role="tablist">
                {[
                  { key: "users",    label: "By User",    icon: "ph-user-list"    },
                  { key: "services", label: "By Service", icon: "ph-squares-four" },
                ].map(({ key, label, icon }) => (
                  <li className="nav-item" key={key}>
                    <button className={`nav-link${activeTab===key?" active":""}`}
                      onClick={() => setActiveTab(key)}>
                      <i className={`ph-bold ${icon} pe-1`}></i>{label}
                    </button>
                  </li>
                ))}
              </ul>
              <span className="text-secondary f-s-12">
                {data.from} — {data.to} · {data.total_users} user{data.total_users!==1?"s":""}
              </span>
            </div>

            <div className="card-body p-0">

              {/* ── By User tab ── */}
              {activeTab === "users" && (
                <>
                  <div className="table-responsive">
                    {report.length === 0 ? (
                      <div className="text-center py-5 text-secondary">
                        <i className="ph-duotone ph-users f-s-48 d-block mb-2"></i>
                        No transactions found for this period
                      </div>
                    ) : (
                      <table className="table table-hover mb-0">
                        <thead style={{ background: "#f8f9fc" }}>
                          <tr>
                            <th className="f-s-12 f-w-700 text-secondary">User</th>
                            <th className="f-s-12 f-w-700 text-secondary">Role</th>
                            <th className="f-s-12 f-w-700 text-secondary">Total Txn</th>
                            <th className="f-s-12 f-w-700 text-secondary">Success / Failed</th>
                            <th className="f-s-12 f-w-700 text-secondary">Opening Bal</th>
                            <th className="f-s-12 f-w-700 text-primary">Closing Bal</th>
                            <th className="f-s-12 f-w-700 text-success">Fund In</th>
                            <th className="f-s-12 f-w-700 text-danger">Fund Out</th>
                            <th className="f-s-12 f-w-700 text-secondary">Svc Amount</th>
                            <th className="f-s-12 f-w-700 text-success">Success Amt</th>
                            <th className="f-s-12 f-w-700 text-primary">Commission</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map(user => (
                            <UserRow key={user.user_id} user={user}
                              isOpen={expandedKey === user.user_id}
                              onToggle={() => setExpandedKey(expandedKey===user.user_id ? null : user.user_id)} />
                          ))}
                        </tbody>
                        <tfoot style={{ background: "#eef2ff", borderTop: "2px solid #c7d2fe" }}>
                          <tr>
                            <td colSpan={2} className="f-s-12 f-w-700 text-primary">
                              TOTAL ({report.length} users)
                            </td>
                            <td className="f-s-13 f-w-700 text-secondary">
                              {(g.total_txn||0).toLocaleString("en-IN")}
                            </td>
                            <td className="f-s-13 f-w-700 text-success">
                              {(g.success_txn||0).toLocaleString("en-IN")} /{" "}
                              <span className="text-danger">{(g.failed_txn||0).toLocaleString("en-IN")}</span>
                            </td>
                            <td className="f-s-12 text-secondary">—</td>
                            <td className="f-s-12 text-secondary">—</td>
                            <td className="f-s-13 f-w-700 text-success">{inr(g.total_fund_in)}</td>
                            <td className="f-s-13 f-w-700 text-danger">{inr(g.total_fund_out)}</td>
                            <td className="f-s-13 f-w-700 text-secondary">{inr(g.total_amount)}</td>
                            <td className="f-s-13 f-w-700 text-success">{inr(g.success_amount)}</td>
                            <td className="f-s-13 f-w-700 text-primary">{inr(g.total_commission)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {report.length > PAGE_SIZE && (
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
                      <span className="text-secondary f-s-13">
                        Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,report.length)} of {report.length}
                      </span>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-primary" disabled={page===1}
                          onClick={() => setPage(p => p-1)}>
                          <i className="ph-bold ph-caret-left"></i>
                        </button>
                        {Array.from({length:Math.min(Math.ceil(report.length/PAGE_SIZE),7)},(_,i)=>{
                          const pg=Math.max(1,page-3)+i;
                          if(pg>Math.ceil(report.length/PAGE_SIZE)) return null;
                          return <button key={pg}
                            className={`btn btn-sm ${pg===page?"btn-primary":"btn-outline-primary"}`}
                            onClick={()=>setPage(pg)}>{pg}</button>;
                        })}
                        <button className="btn btn-sm btn-outline-primary"
                          disabled={page*PAGE_SIZE>=report.length}
                          onClick={() => setPage(p => p+1)}>
                          <i className="ph-bold ph-caret-right"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── By Service tab ── */}
              {activeTab === "services" && (
                <div className="table-responsive">
                  {serviceSummary.length === 0 ? (
                    <div className="text-center py-5 text-secondary">
                      <i className="ph-duotone ph-squares-four f-s-48 d-block mb-2"></i>No data
                    </div>
                  ) : (
                    <table className="table table-hover mb-0">
                      <thead style={{ background: "#f8f9fc" }}>
                        <tr>
                          <th className="f-s-12 f-w-700 text-secondary">Service</th>
                          <th className="f-s-12 f-w-700 text-secondary">Total Txn</th>
                          <th className="f-s-12 f-w-700 text-success">Success</th>
                          <th className="f-s-12 f-w-700 text-danger">Failed</th>
                          <th className="f-s-12 f-w-700 text-warning">Pending</th>
                          <th className="f-s-12 f-w-700 text-secondary">Total Amount</th>
                          <th className="f-s-12 f-w-700 text-success">Success Amt</th>
                          <th className="f-s-12 f-w-700 text-primary">Commission</th>
                          <th className="f-s-12 f-w-700 text-secondary">Top Operators</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serviceSummary.map(sv => (
                          <tr key={sv.slug}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <i className={`ph-duotone ${sv.icon} f-s-18 text-primary`}></i>
                                <span className="f-w-600 f-s-13">{sv.label}</span>
                              </div>
                            </td>
                            <td><span className="badge text-light-primary f-s-12 px-2">{sv.total_txn.toLocaleString("en-IN")}</span></td>
                            <td className="text-success f-w-700">{sv.success_txn.toLocaleString("en-IN")}</td>
                            <td className="text-danger f-w-600">{sv.failed_txn.toLocaleString("en-IN")}</td>
                            <td className="text-warning f-w-600">{sv.pending_txn.toLocaleString("en-IN")}</td>
                            <td className="f-w-600 text-secondary">{inr(sv.total_amount)}</td>
                            <td className="f-w-700 text-success">{inr(sv.success_amount)}</td>
                            <td className="f-w-700 text-primary">{inr(sv.commission)}</td>
                            <td style={{ minWidth: 180 }}>
                              {Object.entries(sv.operators||{}).sort((a,b)=>b[1].amount-a[1].amount)
                                .slice(0,3).map(([op,v],i)=>(
                                <div key={i} className="d-flex justify-content-between gap-3">
                                  <span className="text-secondary f-s-11">{op}</span>
                                  <span className="f-s-11 f-w-500 text-primary">{v.txn} · {inr(v.amount)}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot style={{ background: "#eef2ff", borderTop: "2px solid #c7d2fe" }}>
                        <tr>
                          <td className="f-s-12 f-w-700 text-primary">TOTAL ({serviceSummary.length} services)</td>
                          <td className="f-s-13 f-w-700 text-secondary">{(g.total_txn||0).toLocaleString("en-IN")}</td>
                          <td className="f-s-13 f-w-700 text-success">{(g.success_txn||0).toLocaleString("en-IN")}</td>
                          <td className="f-s-13 f-w-700 text-danger">{(g.failed_txn||0).toLocaleString("en-IN")}</td>
                          <td className="f-s-13 f-w-700 text-warning">{(g.pending_txn||0).toLocaleString("en-IN")}</td>
                          <td className="f-s-13 f-w-700 text-secondary">{inr(g.total_amount)}</td>
                          <td className="f-s-13 f-w-700 text-success">{inr(g.success_amount)}</td>
                          <td className="f-s-13 f-w-700 text-primary">{inr(g.total_commission)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!data && !loading && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ph-duotone ph-chart-bar f-s-64 text-primary d-block mb-3" style={{ opacity: 0.4 }}></i>
              <h5 className="text-secondary f-w-500">Select a date range and click Fetch Report</h5>
              <p className="text-secondary f-s-13">
                Shows every service with operator breakdown, daily opening &amp; closing balance
                from all transactions, fund transfers in/out, and full wallet reconciliation per retailer.
              </p>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

export default UserWiseServiceReportPage;